import { Tabs, Tooltip, rem } from '@mantine/core'
import { useSessionStorage } from '@mantine/hooks'
import { useEffect } from 'react'
import { FormattedMessage } from 'react-intl'
import { useNavigate } from 'react-router'
import { useDeviceDetect } from '@/hooks/useDeviceDetect'
import { useApplicationContext } from '@/providers/ApplicationContext'
import { buildCreateDeploymentLink } from '@/router/paths'
import { Application, ApplicationType } from '@/types/app'
import { MLModelType } from '@/types/model'
import { PageWithFixedToolbar } from '../layout/PageWithFixedToolbar/PageWithFixedToolbar'
import { PlatformAlert } from '../ui-shared/PlatformAlert/PlatformAlert'
import { ModelList } from './ModelList/ModelList'
import { ModelListTab } from './ModelList/ModelListTab'
import { useModelListStatus } from './ModelList/useModelListStatus'
import { ModelListToolbar } from './ModelListToolbar/ModelListToolbar'

const STORAGE_KEY = 'model-list-active-tab'
const getStorageKey = (appId: string) => `${STORAGE_KEY}-${appId}`

type IsDeployDisabledParams = {
  primaryModelCount: number
  secondaryModelCount: number
  appType: ApplicationType
}

const isDeployDisabled = ({
  primaryModelCount,
  secondaryModelCount,
  appType
}: IsDeployDisabledParams) => {
  if (appType === ApplicationType.SingleStage) {
    return primaryModelCount === 0
  }

  if (appType === ApplicationType.MultiStage) {
    return primaryModelCount === 0 || secondaryModelCount === 0
  }

  return true
}

export const ModelsPage = () => {
  const navigate = useNavigate()
  const { isMobile } = useDeviceDetect()

  const { application } = useApplicationContext()
  const { application_type } = application as Application

  const applicationId = application?.id || ''

  // Persist the active tab in session storage to maintain state when navigating back to the model list
  const [activeTab, setActiveTab] = useSessionStorage({
    key: getStorageKey(applicationId),
    defaultValue: String(MLModelType.Primary)
  })

  const { primaryModelCount, secondaryModelCount, isStatusLoading } =
    useModelListStatus(applicationId)

  const isSecondStageDisabled =
    primaryModelCount === 0 && secondaryModelCount === 0 && !isStatusLoading

  // Clear other model list tabs when switching between applications
  useEffect(() => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith(STORAGE_KEY) && key !== getStorageKey(applicationId)) {
        sessionStorage.removeItem(key)
      }
    })
  }, [applicationId])

  const handleOnDeploy = () => {
    void navigate(buildCreateDeploymentLink(applicationId))
  }

  return (
    <PageWithFixedToolbar
      toolbar={
        <ModelListToolbar
          isMultiStage={application_type === ApplicationType.MultiStage}
          isDeployDisabled={isDeployDisabled({
            primaryModelCount,
            secondaryModelCount,
            appType: application_type
          })}
          isLoading={isStatusLoading}
          onDeploy={handleOnDeploy}
        />
      }
    >
      {application_type === ApplicationType.SingleStage && (
        <ModelList
          applicationType={application_type}
          modelType={MLModelType.Primary}
          applicationId={applicationId}
          addModelLabel={<FormattedMessage id="models.list.new" />}
        />
      )}

      {application_type === ApplicationType.MultiStage && (
        <>
          {isSecondStageDisabled && (
            <PlatformAlert variant="info" mb="xl">
              <FormattedMessage id="models.list.multiStageInfo" />
            </PlatformAlert>
          )}

          <Tabs
            value={activeTab}
            color="brand-primary.3"
            variant="pills"
            orientation={isMobile ? 'horizontal' : 'vertical'}
            styles={(theme) => ({
              list: {
                marginRight: theme.spacing.md,
                marginBottom: theme.spacing.md
              },
              tab: {
                minWidth: rem(140)
              }
            })}
            onChange={(val) => setActiveTab(val as string)}
          >
            <Tabs.List>
              <ModelListTab
                value={String(MLModelType.Primary)}
                label={<FormattedMessage id="models.type.stage1" />}
                isCompleted={primaryModelCount > 0}
              />

              <ModelListTab
                value={String(MLModelType.Secondary)}
                label={
                  <Tooltip
                    w={240}
                    disabled={primaryModelCount > 0 || secondaryModelCount > 0}
                    label={<FormattedMessage id="models.list.stage2.unlock" />}
                    position="bottom"
                    multiline
                  >
                    <div>
                      <FormattedMessage id="models.type.stage2" />
                    </div>
                  </Tooltip>
                }
                isCompleted={secondaryModelCount > 0}
                isDisabled={isSecondStageDisabled}
              />
            </Tabs.List>

            <Tabs.Panel value={String(MLModelType.Primary)}>
              <ModelList
                applicationType={application_type}
                modelType={MLModelType.Primary}
                applicationId={applicationId}
                addModelLabel={<FormattedMessage id="models.list.new.stage1" />}
              />
            </Tabs.Panel>

            <Tabs.Panel value={String(MLModelType.Secondary)}>
              <ModelList
                applicationType={application_type}
                modelType={MLModelType.Secondary}
                applicationId={applicationId}
                addModelLabel={<FormattedMessage id="models.list.new.stage2" />}
              />
            </Tabs.Panel>
          </Tabs>
        </>
      )}
    </PageWithFixedToolbar>
  )
}
